import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, ArrowRight, ArrowDown } from "lucide-react";
import { Reveal, MaskLines, MaskLinesInView } from "@/components/Reveal";
import BrandMarquee from "@/components/BrandMarquee";
import CTASection from "@/components/CTASection";
import Testimonials from "@/components/Testimonials";
import { PILLARS, SERVICES, SCOTT } from "@/lib/data";
import scottImg from "@/assets/brand/scott.png";
import lighthouseImg from "@/assets/brand/lighthouse_hero.webp";

const HERO_IMG = lighthouseImg;
const OFFICE_IMG =
  "https://images.unsplash.com/photo-1706074740295-d7a79c079562?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "24%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section ref={ref} data-testid="hero" className="relative bg-cream grain overflow-hidden">
      <div className="container-editorial relative z-10 pt-40 pb-20 md:pt-52 md:pb-28 grid lg:grid-cols-[1.15fr_0.85fr] gap-12 lg:gap-16 items-center">
        <div>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
            <p className="eyebrow text-amber-deep mb-8 flex items-center gap-3">
              <span className="w-10 h-px bg-amber-deep inline-block" />
              From Start-Up to Exit
            </p>
          </motion.div>

          <h1 className="font-serif font-bold text-5xl sm:text-6xl lg:text-7xl xl:text-8xl leading-[0.98] tracking-tight text-blue">
            <MaskLines lines={["From your first day"]} delay={0.15} />
            <MaskLines
              lines={["to your best last day."]}
              className="text-amber-deep italic"
              delay={0.32}
            />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.7 }}
            className="font-body text-lg text-charcoal/75 max-w-xl mt-8 leading-relaxed"
          >
            Logan Hunter Group is one advisor with the experience of a full team, carrying you from
            launch, growth, and funding all the way to a maximized exit.{" "}
            <span className="text-blue font-bold">One team. One fee.</span>
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link
              to="/contact"
              data-testid="hero-cta-consultation"
              className="group relative overflow-hidden bg-blue text-cream px-8 py-4 eyebrow text-xs flex items-center justify-center gap-2"
            >
              <span className="relative z-10 flex items-center gap-2">
                Book a Consultation <ArrowUpRight size={16} />
              </span>
              <span className="absolute inset-0 bg-amber-deep translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-services"
              className="border border-blue/25 text-blue px-8 py-4 eyebrow text-xs flex items-center justify-center gap-2 hover:border-blue hover:bg-blue hover:text-cream transition-colors"
            >
              Explore the Journey <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>

        <motion.div style={{ opacity: fade }} className="relative hidden lg:block">
          <div className="relative h-[560px] overflow-hidden">
            <div className="absolute -inset-3 border border-amber/40 z-20 pointer-events-none" />
            <motion.img
              style={{ y: imgY }}
              src={HERO_IMG}
              alt="A lighthouse at sunset, steady guidance from first light to the horizon"
              className="absolute inset-0 w-full h-[130%] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-blue/70 via-transparent to-transparent z-10" />
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4, duration: 1 }}
        className="container-editorial relative z-10 pb-8 flex items-center gap-3 text-blue/50"
      >
        <ArrowDown size={16} className="animate-bounce" />
        <span className="eyebrow text-[0.6rem]">Scroll, the journey begins</span>
      </motion.div>
    </section>
  );
}

function Pillars() {
  return (
    <section data-testid="pillars" className="bg-cream py-24 md:py-36">
      <div className="container-editorial">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-10 lg:gap-20 mb-16 lg:mb-24">
          <div>
            <Reveal>
              <p className="eyebrow text-amber-deep mb-6">Why It Works</p>
            </Reveal>
            <MaskLinesInView
              lines={["One advisor.", "The work of a"]}
              className="font-serif font-bold text-4xl md:text-5xl lg:text-6xl text-blue leading-[1.02]"
            />
            <MaskLinesInView
              lines={["whole team."]}
              className="font-serif font-bold italic text-4xl md:text-5xl lg:text-6xl text-amber-deep leading-[1.02]"
            />
          </div>
          <Reveal delay={0.15} className="self-end">
            <p className="font-body text-lg text-charcoal/75 leading-relaxed max-w-xl">
              Instead of hiring a valuator, a plan writer, a marketing strategist, a developer, and a
              financial analyst separately, each at their own rate, you get all of it from one
              coordinated advisor. Done-for-you, bank-ready, and delivered in weeks.
            </p>
          </Reveal>
        </div>

        <div className="grid md:grid-cols-2 gap-px bg-blue/10 border border-blue/10">
          {PILLARS.map((p, i) => (
            <Reveal
              key={p.n}
              delay={i * 0.08}
              className="group bg-cream p-9 md:p-12 hover:bg-blue transition-colors duration-500"
            >
              <div className="flex items-start gap-6">
                <span className="font-serif font-bold text-5xl md:text-6xl text-amber/50 group-hover:text-amber-bright transition-colors duration-500 leading-none">
                  {p.n}
                </span>
                <div>
                  <h3 className="font-serif font-bold text-2xl md:text-3xl text-blue group-hover:text-cream transition-colors duration-500">
                    {p.title}
                  </h3>
                  <p className="font-body text-charcoal/70 group-hover:text-cream/75 transition-colors duration-500 mt-4 leading-relaxed">
                    {p.body}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function Journey() {
  return (
    <section data-testid="journey" className="bg-blue-deep text-cream py-24 md:py-36 grain relative overflow-hidden">
      <div className="container-editorial relative z-10">
        <div className="max-w-3xl mb-16">
          <Reveal>
            <p className="eyebrow text-amber-bright mb-6">The Whole Journey</p>
          </Reveal>
          <MaskLinesInView
            lines={["Every stage of a", "business's life,"]}
            className="font-serif font-bold text-4xl md:text-6xl text-cream leading-[1.03]"
          />
          <MaskLinesInView
            lines={["one coherent path."]}
            className="font-serif font-bold italic text-4xl md:text-6xl text-amber-bright leading-[1.03]"
          />
          <Reveal delay={0.15}>
            <p className="font-body text-cream/65 mt-8 text-lg leading-relaxed">
              Plan backward from the exit. It isn't the caboose; it's the summit every other service
              climbs toward. Name it up front, then build the path that leads there.
            </p>
          </Reveal>
        </div>

        <div className="border-t border-cream/10">
          {SERVICES.map((s, i) => (
            <Reveal key={s.slug} delay={i * 0.04}>
              <Link
                to={s.slug === "exit-on-your-terms" ? "/services/exit-and-sell" : `/services/${s.slug}`}
                data-testid={`journey-link-${s.slug}`}
                className="group flex items-center justify-between gap-6 py-7 border-b border-cream/10 hover:px-4 transition-[padding] duration-500"
              >
                <div className="flex items-baseline gap-6">
                  <span className="font-serif text-xl text-amber/60 w-10">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-serif font-bold text-3xl md:text-5xl text-cream group-hover:text-amber-bright transition-colors duration-400">
                    {s.code}
                  </span>
                </div>
                <div className="flex items-center gap-6">
                  <span className="hidden md:block font-body text-sm text-cream/45 max-w-xs text-right">
                    {s.tagline}
                  </span>
                  <ArrowUpRight
                    size={26}
                    className="text-cream/40 group-hover:text-amber-bright group-hover:rotate-45 transition-all duration-400"
                  />
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

function MeetScott() {
  return (
    <section data-testid="meet-scott" className="bg-cream py-24 md:py-36">
      <div className="container-editorial grid lg:grid-cols-[0.8fr_1.2fr] gap-12 lg:gap-20 items-center">
        <Reveal className="relative">
          <div className="relative max-w-sm mx-auto lg:mx-0">
            <div className="absolute -inset-3 border border-amber/40" />
            <div className="absolute -bottom-5 -right-5 bg-blue text-cream px-6 py-4 z-20">
              <p className="font-serif font-bold text-4xl leading-none">34+</p>
              <p className="eyebrow text-[0.55rem] text-amber-bright mt-1">Years</p>
            </div>
            <img src={scottImg} alt="Scott Bucher" className="relative w-full object-cover grayscale-[10%]" />
          </div>
        </Reveal>
        <div>
          <Reveal>
            <p className="eyebrow text-amber-deep mb-6">Meet Your Advisor</p>
          </Reveal>
          <MaskLinesInView lines={["Scott Bucher"]} className="font-serif font-bold text-5xl md:text-6xl text-blue" />
          <Reveal delay={0.1}>
            <p className="font-body text-slate mt-2">{SCOTT.title}</p>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="font-serif italic text-2xl md:text-3xl text-blue mt-8 leading-snug max-w-xl">
              &ldquo;{SCOTT.quote}&rdquo;
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="font-body text-charcoal/75 mt-6 leading-relaxed max-w-xl">
              Mentored directly by Tom Hopkins, forged by athletic discipline, and 34+ years deep in
              business development, Scott is the trust anchor behind every engagement.
            </p>
          </Reveal>
          <Reveal delay={0.25}>
            <Link
              to="/about"
              data-testid="meet-scott-cta"
              className="mt-8 inline-flex items-center gap-2 text-blue eyebrow text-xs link-underline hover:text-amber-deep transition-colors"
            >
              Read Scott's Story <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function ProofBand() {
  const stats = [
    { v: "10", l: "Experts in one" },
    { v: "1", l: "Coordinated fee" },
    { v: "~90%", l: "Typical savings" },
    { v: "Weeks", l: "Not a year+" },
  ];
  return (
    <section data-testid="proof-band" className="relative overflow-hidden">
      <div className="absolute inset-0">
        <img src={OFFICE_IMG} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-blue-deep/92" />
      </div>
      <div className="container-editorial relative z-10 py-20 md:py-28 grain">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-y-12 gap-x-6">
          {stats.map((s, i) => (
            <Reveal key={s.l} delay={i * 0.08} className="text-center md:text-left">
              <p className="font-serif font-bold text-5xl md:text-7xl text-amber-bright leading-none">{s.v}</p>
              <p className="eyebrow text-white text-sm md:text-base mt-4">{s.l}</p>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <>
      <Hero />
      <BrandMarquee />
      <Pillars />
      <Journey />
      <MeetScott />
      <Testimonials />
      <ProofBand />
      <CTASection />
    </>
  );
}
